import get from 'just-safe-get';
import set from 'just-safe-set';
import { CompanionError } from '../error/CompanionError';
/**
 * Abstract class containing the 2 utility methods for conversion
 */
export class AbstractIOConverter {
}
/**
 * Convert input from the client to a format that is consumable by the server
 */
export function convertToServerFormat(input, conversionConfig) {
    const output = {}; // We have to type-juggle here but the output type is restricted properly
    for (const key in input) {
        if (typeof input[key] === 'function') {
            // Do not attempt to convert functions
            continue;
        }
        const originalValue = input[key];
        if (key in conversionConfig) {
            const conversionClass = conversionConfig[key];
            if (conversionClass) {
                output[key] = conversionClass.toServerFormat(originalValue);
            }
        }
        else {
            output[key] = originalValue;
        }
    }
    return output;
}
/**
 * Convert the response from the server to a format that is more easily usable by the client
 */
export function convertToClientFormat(input, conversionConfig) {
    const output = {}; // We have to type-juggle here but the output type is restricted properly
    for (const key in input) {
        if (typeof input[key] === 'function') {
            // Do not attempt to convert functions
            continue;
        }
        const originalValue = input[key];
        if (key in conversionConfig) {
            const conversionClass = conversionConfig[key];
            if (conversionClass) {
                output[key] = conversionClass.toClientFormat(originalValue);
            }
        }
        else {
            output[key] = originalValue;
        }
    }
    return output;
}
const inObj = (path, obj) => {
    if (!Array.isArray(path)) {
        path = path.split('.');
    }
    const key = path.shift();
    if (key && key in obj) {
        if (typeof obj[key] === 'object' && path.length) {
            // Recurse
            return inObj(path, obj[key]);
        }
        return true;
    }
    return false;
};
/**
 * Convert data from server <-> client
 */
export function convert(method, input, conversionConfig) {
    const convertFnName = method === 'toServer' ? 'toServerFormat' : method === 'toClient' ? 'toClientFormat' : undefined;
    if (!convertFnName) {
        throw new CompanionError({
            error: new Error('Method must be `toClient` or `toServer`'),
            code: 'METHOD_INVALID',
        });
    }
    // Simplify the conversion with this type cast
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const output = Object.assign({}, input);
    for (const path in conversionConfig) {
        // Check that the path is actually in the object
        if (!inObj(path, output)) {
            continue;
        }
        const conversionClass = conversionConfig[path];
        const originalValue = get(output, path);
        if (conversionClass) {
            // Update output object at path
            set(output, path, conversionClass[convertFnName](originalValue));
        }
    }
    return output;
}
convert.ERR = {
    METHOD_INVALID: 'METHOD_INVALID',
};
/**
 * Omit properties from object based on assertion function
 *
 * Returns new object
 */
export const omitFromObject = (obj, assertFn) => {
    const ret = Object.assign({}, obj);
    for (const key in obj) {
        if (assertFn(obj[key], key)) {
            // Only add to new object if fn returns truthy
            ret[key] = obj[key];
        }
    }
    return ret;
};
